import { useState, useEffect } from 'react';
import './PrinterManagementModal.css';
import PrinterImage from './PrinterImage';
import { CommandIconSVG, RibbonIconSVG, CardIconSVG } from './PrinterSVG';

interface PrinterManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
    printer: {
        id?: string;
        name: string;
        ip_address: string;
        serial_number?: string;
        firmware_version?: string;
    };
    serverUrl: string;
    onDelete: (id: string) => void;
}

interface PrinterStatus {
    printer_model?: string;
    printer_ready?: boolean;
    card_present?: boolean;
    ribbon_ok?: boolean;
    ribbon_remaining?: number;
    ribbon_type?: string;
    led_status?: string;
    is_printing?: boolean;
    error_status?: string;
}

function PrintHistoryTable({ serverUrl, serialNumber }: { serverUrl: string, serialNumber?: string }) {
    const [history, setHistory] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (serialNumber) {
            fetchHistory();
        }
    }, [serialNumber]);

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${serverUrl}/api/printer/history?serial_number=${serialNumber}`);
            const data = await res.json();
            if (data.success) {
                setHistory(data.history);
            }
        } catch (error) {
            console.error('Failed to fetch history', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '10px' }}>Loading history...</div>;
    if (history.length === 0) return <div style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '10px' }}>No print history found.</div>;

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', color: '#e2e8f0' }}>
                <thead>
                    <tr style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.2)', textAlign: 'left' }}>
                        <th style={{ padding: '8px', color: '#94a3b8' }}>Date</th>
                        <th style={{ padding: '8px', color: '#94a3b8' }}>Status</th>
                        <th style={{ padding: '8px', color: '#94a3b8' }}>Details</th>
                    </tr>
                </thead>
                <tbody>
                    {history.map((job) => (
                        <tr key={job.id} style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.1)' }}>
                            <td style={{ padding: '8px' }}>{new Date(job.date).toLocaleString()}</td>
                            <td style={{ padding: '8px' }}>
                                <span style={{
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    background: job.status === 'completed' ? 'rgba(74, 222, 128, 0.1)' : 'rgba(248, 113, 113, 0.1)',
                                    color: job.status === 'completed' ? '#4ade80' : '#f87171',
                                    fontWeight: '600',
                                    fontSize: '11px'
                                }}>
                                    {job.status.toUpperCase()}
                                </span>
                            </td>
                            <td style={{ padding: '8px' }}>
                                {job.details.ribbon_type && <span style={{ marginRight: '5px', background: '#334155', padding: '2px 4px', borderRadius: '3px' }}>{job.details.ribbon_type}</span>}
                                {job.details.double_sided && <span style={{ background: '#334155', padding: '2px 4px', borderRadius: '3px' }}>2-Sided</span>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function PrinterManagementModal({
    isOpen,
    onClose,
    printer,
    serverUrl,
    onDelete
}: PrinterManagementModalProps) {
    const [status, setStatus] = useState<PrinterStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [commandLoading, setCommandLoading] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (isOpen) {
            fetchStatus();
            const interval = setInterval(fetchStatus, 5000);
            return () => clearInterval(interval);
        }
    }, [isOpen]);

    const fetchStatus = async () => {
        setLoading(true);
        try {
            const connectRes = await fetch(`${serverUrl}/api/printer/connect`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ printer_name: printer.name })
            });

            if (!connectRes.ok) {
                setMessage('Failed to connect to printer');
                setLoading(false);
                return;
            }

            const statusRes = await fetch(`${serverUrl}/api/printer/status`);
            const data = await statusRes.json();

            if (data.success) {
                setStatus(data);
                setMessage('');
            } else {
                setMessage(data.message || 'Failed to get status');
            }
        } catch (error) {
            setMessage('Connection error');
        } finally {
            setLoading(false);
        }
    };

    const executeCommand = async (command: string) => {
        setCommandLoading(true);
        setMessage('');
        try {
            const res = await fetch(`${serverUrl}/api/printer/command`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    command: command,
                    printer_name: printer.name,
                    serial_number: printer.serial_number
                })
            });

            const data = await res.json();
            setMessage(data.success ? `✅ ${data.message}` : `❌ ${data.message}`);

            if (data.success) {
                setTimeout(fetchStatus, 1000);
            }
        } catch (error) {
            setMessage('❌ Command failed');
        } finally {
            setCommandLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!confirm(`Are you sure you want to remove ${printer.name} from your account?`)) {
            return;
        }

        try {
            const id = printer.id || printer.serial_number;
            const res = await fetch(`${serverUrl}/api/printer/unregister/${id}`, {
                method: 'DELETE'
            });

            const data = await res.json();
            if (data.success) {
                setMessage('✅ Printer removed successfully');
                setTimeout(() => {
                    if (id) onDelete(id);
                    onClose();
                }, 1500);
            } else {
                setMessage(`❌ ${data.message}`);
            }
        } catch (error) {
            setMessage('❌ Failed to remove printer');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content printer-mgmt-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Printer Management</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="modal-body">
                    {/* Header Block - Forced Horizontal Layout via Inline Styles */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: '20px',
                        padding: '15px',
                        background: 'rgba(30, 41, 59, 0.6)',
                        borderRadius: '12px',
                        marginBottom: '20px',
                        border: '1px solid rgba(148, 163, 184, 0.1)'
                    }}>
                        {/* Left: Image */}
                        <div style={{
                            flex: '0 0 120px',
                            width: '120px',
                            height: '120px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '8px',
                            border: '1px solid rgba(148, 163, 184, 0.1)',
                            padding: '10px'
                        }}>
                            <PrinterImage model={printer.name} size="medium" />
                        </div>

                        {/* Right: Info */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <h3 style={{
                                margin: '0 0 15px 0',
                                fontSize: '14px',
                                fontWeight: '700',
                                color: '#f1f5f9',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
                                paddingBottom: '8px'
                            }}>
                                Printer Information
                            </h3>

                            <div style={{ display: 'grid', gap: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                    <span style={{ color: '#94a3b8' }}>Printer Name:</span>
                                    <span style={{ color: '#e2e8f0', fontWeight: '600' }}>{printer.name}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                    <span style={{ color: '#94a3b8' }}>Connection:</span>
                                    <span style={{ color: '#60a5fa', fontFamily: 'monospace' }}>{printer.ip_address}</span>
                                </div>
                                {printer.serial_number && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                        <span style={{ color: '#94a3b8' }}>Serial Number:</span>
                                        <span style={{ color: '#e2e8f0', fontFamily: 'monospace' }}>{printer.serial_number}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Minimalist Status Bar */}
                    <div className="status-section">
                        <div className="section-header" style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>REAL-TIME STATUS</h3>
                            <button
                                onClick={fetchStatus}
                                disabled={loading}
                                className="btn-refresh"
                                style={{ padding: '4px 8px', fontSize: '0.7rem' }}
                            >
                                {loading ? '⟳' : '🔄'} Refresh
                            </button>
                        </div>

                        {status && (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                                gap: '10px',
                                background: 'rgba(30, 41, 59, 0.3)',
                                borderRadius: '10px',
                                padding: '12px',
                                border: '1px solid rgba(148, 163, 184, 0.1)'
                            }}>
                                {/* Printer Status */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                                    <div style={{ fontSize: '18px' }}>{status.printer_ready ? '✅' : '⚠️'}</div>
                                    <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600' }}>Status</div>
                                    <div style={{ fontSize: '12px', fontWeight: '700', color: status.printer_ready ? '#4ade80' : '#f87171' }}>
                                        {status.led_status || 'Unknown'}
                                    </div>
                                </div>

                                {/* Ribbon */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                                    <div style={{ fontSize: '18px' }}><RibbonIconSVG /></div>
                                    <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600' }}>Ribbon</div>
                                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#e2e8f0', textAlign: 'center', lineHeight: '1.1' }}>
                                        {status.ribbon_remaining || 0}%
                                    </div>
                                </div>

                                {/* Card */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                                    <div style={{ fontSize: '18px' }}><CardIconSVG /></div>
                                    <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600' }}>Card</div>
                                    <div style={{ fontSize: '12px', fontWeight: '700', color: status.card_present ? '#60a5fa' : '#94a3b8' }}>
                                        {status.card_present ? 'Present' : 'Empty'}
                                    </div>
                                </div>

                                {/* Activity */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                                    <div style={{ fontSize: '18px' }}>🖨️</div>
                                    <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600' }}>Activity</div>
                                    <div style={{ fontSize: '12px', fontWeight: '700', color: status.is_printing ? '#facc15' : '#94a3b8' }}>
                                        {status.is_printing ? 'Printing' : 'Idle'}
                                    </div>
                                </div>
                            </div>
                        )}

                        {message && (
                            <div className={`message ${message.includes('❌') ? 'error' : 'success'}`} style={{ marginTop: '10px', padding: '8px', fontSize: '12px' }}>
                                {message}
                            </div>
                        )}
                    </div>

                    <div className="commands-section">
                        <h3>Printer Commands</h3>
                        <div className="command-buttons">
                            <button
                                onClick={() => executeCommand('feed')}
                                disabled={commandLoading}
                                className="btn-command"
                            >
                                <CommandIconSVG type="feed" /> Feed Card
                            </button>
                            <button
                                onClick={() => executeCommand('eject')}
                                disabled={commandLoading}
                                className="btn-command"
                            >
                                <CommandIconSVG type="eject" /> Eject Card
                            </button>
                            <button
                                onClick={() => executeCommand('reject')}
                                disabled={commandLoading}
                                className="btn-command"
                            >
                                <CommandIconSVG type="reject" /> Reject Card
                            </button>
                            <button
                                onClick={() => executeCommand('reset')}
                                disabled={commandLoading}
                                className="btn-command"
                            >
                                <CommandIconSVG type="reset" /> Reset Printer
                            </button>
                        </div>
                        <p style={{
                            marginTop: '12px',
                            fontSize: '0.85rem',
                            color: '#64748b',
                            textAlign: 'center'
                        }}>
                            💡 Tip: Use "Feed" to load a card, "Eject" to output it, or "Reset" if the printer is stuck
                        </p>
                    </div>

                    <div className="history-section" style={{ marginTop: '20px', background: 'rgba(30, 41, 59, 0.3)', borderRadius: '10px', padding: '15px', border: '1px solid rgba(148, 163, 184, 0.1)' }}>
                        <h3 style={{ margin: '0 0 15px 0', fontSize: '14px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>
                            Print History
                        </h3>
                        <PrintHistoryTable serverUrl={serverUrl} serialNumber={printer.serial_number} />
                    </div>

                    <div className="danger-section">
                        <h3>⚠️ Danger Zone</h3>
                        <button onClick={handleDelete} className="btn-delete">
                            🗑️ Remove Printer from Account
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
